// src/components/IPDDischargeBilling.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { 
  getIPDServices, 
  generateIPDDischargeBill,
  processCashPayment,
  processCreditPayment,
  updateBill
} from '../services/billingService';
import { createInvoice as createInvoiceRecord } from '../services/invoiceService';
import { getTariffForBilling } from '../services/tariffService';
import { getPatientById } from '../services/patientService';
import { getAssignmentByAdmissionId } from '../services/PatientAssignmentService';
import { getDoctorById } from '../services/doctorService';
import PaymentModeSelector from './PaymentModeSelector';
import { useAuth } from '../context/AuthContext';
import { getOrganizationId, getOrganizationName, fetchOrganizationName } from '../utils/organizationUtils';
import { buildInvoiceDocumentHtml, printPatientSummaryDocument } from '../utils/patientSummaryDocumentUtils';

const IPDDischargeBilling = ({ 
  admissionId, 
  patientId, 
  patientName, 
  onClose, 
  onBillGenerated 
}) => {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState(null);
  const [paymentMode, setPaymentMode] = useState({ mode: 'cash' });
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [tariffInfo, setTariffInfo] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [resolvedPatientName, setResolvedPatientName] = useState(patientName || '');
  const [treatingDoctor, setTreatingDoctor] = useState(null);
  const [organizationName, setOrganizationName] = useState(null);

  useEffect(() => {
    if (admissionId) {
      console.log('[IPDDischargeBilling] useEffect triggered, admissionId:', admissionId);
      fetchIPDServices();
      fetchTreatingDoctor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionId]);

  // Debug: Log services and treating doctor state changes
  useEffect(() => {
    console.log('[IPDDischargeBilling] Services state updated:', services, 'Count:', services?.length || 0);
  }, [services]);

  useEffect(() => {
    console.log('[IPDDischargeBilling] Treating doctor state updated:', treatingDoctor);
  }, [treatingDoctor]);

  // Fetch organization name
  useEffect(() => {
    const fetchOrgName = async () => {
      // First try to get from user object
      const orgName = getOrganizationName(user);
      if (orgName) {
        setOrganizationName(orgName);
        return;
      }
      
      // If not in user object, fetch by organizationId
      const orgId = getOrganizationId(user);
      if (orgId) {
        try {
          const fetchedName = await fetchOrganizationName(orgId);
          if (fetchedName) {
            setOrganizationName(fetchedName);
          }
        } catch (error) {
          console.error('[IPDDischargeBilling] Error fetching organization name:', error);
        }
      }
    };
    
    fetchOrgName();
  }, [user]);

  // Fetch patient name if not provided or is default value
  useEffect(() => {
    const fetchPatientName = async () => {
      // If patientName is not provided, is "Patient" (default), or patientId is available, fetch patient data
      if (patientId && (!patientName || patientName === 'Patient' || patientName.trim() === '')) {
        try {
          const patient = await getPatientById(patientId);
          if (patient) {
            // Use patient.name or construct from firstName/lastName
            const name = patient.name || 
                        (patient.firstName && patient.lastName ? `${patient.firstName} ${patient.lastName}`.trim() : 
                        patient.firstName || patient.lastName || 'N/A');
            if (name && name !== 'Patient') {
              setResolvedPatientName(name);
            }
          }
        } catch (error) {
          console.error('Error fetching patient name:', error);
          // Keep the existing patientName if fetch fails
          if (patientName && patientName !== 'Patient') {
            setResolvedPatientName(patientName);
          }
        }
      } else if (patientName && patientName !== 'Patient') {
        // Use provided patientName if it's valid
        setResolvedPatientName(patientName);
      }
    };

    fetchPatientName();
  }, [patientId, patientName]);

  const fetchIPDServices = async () => {
    setLoading(true);
    try {
      // Get organizationId for organization-specific filtering
      const organizationId = getOrganizationId(user);
      console.log('[IPDDischargeBilling] Fetching services for admission:', admissionId, 'organizationId:', organizationId);
      const data = await getIPDServices(admissionId, organizationId);
      console.log('[IPDDischargeBilling] Services received:', data);
      
      // Handle different response formats
      let servicesArray = [];
      if (Array.isArray(data)) {
        servicesArray = data;
      } else if (data && typeof data === 'object') {
        // Check if data has a services property
        if (Array.isArray(data.services)) {
          servicesArray = data.services;
        } else if (Array.isArray(data.data)) {
          servicesArray = data.data;
        } else {
          // Try to convert object to array
          servicesArray = Object.values(data).filter(item => item && typeof item === 'object');
        }
      }
      
      // Normalize services: extract rate/amount from alternative field names
      const normalizedServices = servicesArray.map(service => {
        if (!service || typeof service !== 'object') return service;
        
        // For lab tests, try multiple field names for rate/amount
        if (service.type === 'lab' || service.type === 'Lab' || service.description?.toLowerCase().includes('lab')) {
          // Log the raw service object to debug
          if (servicesArray.indexOf(service) === 0) {
            console.log('[IPDDischargeBilling] Raw lab test service object:', service);
            console.log('[IPDDischargeBilling] Available fields:', Object.keys(service));
          }
          
          const testPrice = Number(
            service.testPrice || 
            service.rate || 
            service.price || 
            service.amount || 
            service.totalAmount || 
            service.cost || 
            service.testCost ||
            service.labTestPrice ||
            service.testRate ||
            0
          );
          const qty = Number(service.quantity || 1);
          const finalAmount = testPrice * qty || testPrice;
          
          if (servicesArray.indexOf(service) === 0) {
            console.log('[IPDDischargeBilling] Lab test normalized - rate:', testPrice, 'amount:', finalAmount, 'quantity:', qty);
          }
          
          return {
            ...service,
            type: 'lab',
            quantity: qty,
            rate: testPrice,
            amount: finalAmount,
          };
        }
        
        // For other service types, try to extract rate/amount from common field names
        const qty = Number(service.quantity || 1);
        const rate = Number(
          service.rate || 
          service.price || 
          service.cost || 
          service.unitPrice ||
          (service.amount && qty > 0 ? service.amount / qty : 0) ||
          0
        );
        const amount = Number(
          service.amount || 
          service.totalAmount || 
          service.totalCost ||
          (qty * rate) ||
          0
        );
        
        return {
          ...service,
          quantity: qty,
          rate: rate,
          amount: amount,
        };
      });
      
      const normalized = mergeDuplicateServices(normalizedServices);
      console.log('[IPDDischargeBilling] Normalized services:', normalized);
      
      // For lab tests with missing rate/amount, try to fetch from lab test service
      // First, fetch all lab tests for this admission once
      let labTestsForAdmission = null;
      const hasLabTestWithZeroPrice = normalized.some(s => 
        (s.type === 'lab' || s.type === 'Lab') && (!s.rate || s.rate === 0)
      );
      
      if (hasLabTestWithZeroPrice) {
        try {
          console.log('[IPDDischargeBilling] Fetching lab tests for admission:', admissionId);
          const { getLabTestsByAdmissionId } = await import('../services/PatientlabTestService');
          labTestsForAdmission = await getLabTestsByAdmissionId(admissionId);
          console.log('[IPDDischargeBilling] Lab tests fetched for admission:', labTestsForAdmission);
        } catch (error) {
          console.error('[IPDDischargeBilling] Error fetching lab tests:', error);
        }
      }
      
      const servicesWithPrices = normalized.map((service) => {
        // If it's a lab test and rate/amount are 0, try to fetch the actual price
        if ((service.type === 'lab' || service.type === 'Lab') && (!service.rate || service.rate === 0)) {
          if (Array.isArray(labTestsForAdmission) && labTestsForAdmission.length > 0) {
            // If description is generic "Lab Test", aggregate all lab tests or use the first one
            let matchingTest = null;
            let totalPrice = 0;
            let testNames = [];
            
            if (service.description === 'Lab Test' || service.description?.toLowerCase() === 'lab test') {
              // For generic "Lab Test", aggregate all lab tests
              labTestsForAdmission.forEach(t => {
                const testPrice = Number(
                  t.testPrice || 
                  t.rate || 
                  t.amount || 
                  t.totalAmount ||
                  t.price ||
                  t.cost ||
                  0
                );
                if (testPrice > 0) {
                  totalPrice += testPrice;
                  if (t.testName) {
                    testNames.push(t.testName);
                  }
                }
              });
              
              if (totalPrice > 0) {
                console.log('[IPDDischargeBilling] Aggregated lab tests - total price:', totalPrice, 'tests:', testNames);
                return {
                  ...service,
                  description: testNames.length > 0 ? testNames.join(', ') : service.description,
                  rate: totalPrice,
                  amount: totalPrice * (service.quantity || 1),
                };
              } else {
                // Use first test if aggregation didn't work
                matchingTest = labTestsForAdmission[0];
              }
            } else {
              // Try to match by test name
              matchingTest = labTestsForAdmission.find(t => 
                t.testName === service.description || 
                service.description?.includes(t.testName) ||
                t.testName?.includes(service.description)
              );
            }
            
            if (matchingTest) {
              const testPrice = Number(
                matchingTest.testPrice || 
                matchingTest.rate || 
                matchingTest.amount || 
                matchingTest.totalAmount ||
                matchingTest.price ||
                matchingTest.cost ||
                0
              );
              console.log('[IPDDischargeBilling] Matching lab test found:', matchingTest.testName, 'price:', testPrice);
              if (testPrice > 0) {
                const qty = service.quantity || 1;
                console.log('[IPDDischargeBilling] Updating lab test - rate:', testPrice, 'amount:', testPrice * qty, 'for:', service.description);
                return {
                  ...service,
                  description: matchingTest.testName || service.description,
                  rate: testPrice,
                  amount: testPrice * qty,
                };
              } else {
                console.log('[IPDDischargeBilling] Lab test found but price is 0:', matchingTest);
              }
            } else {
              console.log('[IPDDischargeBilling] No matching lab test found for:', service.description);
            }
          }
        }
        return service;
      });
      
      setServices(servicesWithPrices);
      
      // If no services from API, try aggregation fallback
      if (servicesWithPrices.length === 0) {
        console.log('[IPDDischargeBilling] No services from API, attempting to aggregate from existing modules...');
        try {
          const aggregated = await tryAggregateFromExistingModules(admissionId, patientId);
          console.log('[IPDDischargeBilling] Aggregated services:', aggregated);
          if (aggregated && aggregated.length > 0) {
            setServices(mergeDuplicateServices(aggregated));
          }
        } catch (aggregateError) {
          console.error('[IPDDischargeBilling] Error aggregating services:', aggregateError);
        }
      }
    } catch (error) {
      console.error('[IPDDischargeBilling] Error fetching services from API:', error);
      // Fallback: try aggregating from existing modules (lab tests, etc.)
      // These services use apiClient which automatically filters by organizationId
      try {
        console.log('[IPDDischargeBilling] Attempting to aggregate services from existing modules...');
        const aggregated = await tryAggregateFromExistingModules(admissionId, patientId);
        console.log('[IPDDischargeBilling] Aggregated services:', aggregated);
        setServices(mergeDuplicateServices(aggregated));
      } catch (aggregateError) {
        console.error('[IPDDischargeBilling] Error aggregating services:', aggregateError);
        setServices([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTreatingDoctor = async () => {
    try {
      console.log('[IPDDischargeBilling] Fetching treating doctor for admission:', admissionId);
      const assignment = await getAssignmentByAdmissionId(admissionId);
      console.log('[IPDDischargeBilling] Assignment received:', assignment);
      
      // Handle array response (get latest assignment)
      let assignmentData = assignment;
      if (Array.isArray(assignment) && assignment.length > 0) {
        // Sort by id (higher id = more recent) or createdAt
        const sorted = [...assignment].sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt) - new Date(a.createdAt);
          }
          return (b.id || 0) - (a.id || 0);
        });
        assignmentData = sorted[0];
      }
      
      if (assignmentData?.doctorId) {
        // Try to get doctor name from assignment first
        let doctorName = assignmentData.doctorName;
        
        // If doctor name not available (null, undefined, empty string, or 'N/A'), fetch it
        if (!doctorName || doctorName === 'N/A' || doctorName === 'null' || doctorName === null) {
          console.log('[IPDDischargeBilling] Doctor name not available in assignment, fetching doctor by ID:', assignmentData.doctorId);
          try {
            const doctor = await getDoctorById(assignmentData.doctorId);
            console.log('[IPDDischargeBilling] Doctor fetched:', doctor);
            doctorName = doctor?.name || doctor?.fullName || `Dr. ID: ${assignmentData.doctorId}`;
            console.log('[IPDDischargeBilling] Resolved doctor name:', doctorName);
          } catch (doctorError) {
            console.error('[IPDDischargeBilling] Error fetching doctor:', doctorError);
            doctorName = `Dr. ID: ${assignmentData.doctorId}`;
          }
        } else {
          console.log('[IPDDischargeBilling] Using doctor name from assignment:', doctorName);
        }
        
        const doctorInfo = {
          id: assignmentData.doctorId,
          name: doctorName
        };
        console.log('[IPDDischargeBilling] Setting treating doctor state:', doctorInfo);
        setTreatingDoctor(doctorInfo);
      } else {
        console.log('[IPDDischargeBilling] No doctorId in assignment');
        setTreatingDoctor(null);
      }
    } catch (error) {
      console.error('[IPDDischargeBilling] Error fetching treating doctor:', error);
      setTreatingDoctor(null);
    }
  };

  // Attempt to assemble services from existing flows without changing them
  const tryAggregateFromExistingModules = async (admId, pid) => {
    const result = [];
    // 1) Lab tests (IPD)
    try {
      const { getAllIPDLabTests } = await import('../services/PatientlabTestService');
      const rows = await getAllIPDLabTests();
      const list = (Array.isArray(rows) ? rows : []).filter(r => String(r.admissionId) === String(admId));
      for (const t of list) {
        const testPrice = t.testPrice || t.rate || t.amount || t.totalAmount || 0;
        result.push({
          type: 'lab',
          description: t.testName || 'Lab Test',
          quantity: 1,
          rate: testPrice,
          amount: testPrice,
          department: 'Pathology',
        });
      }
    } catch (_) {}

    // 2) Medicines (if transaction service exists)
    try {
      const { getTransactionsByPatientId } = await import('../services/medicineTransactionService');
      if (typeof getTransactionsByPatientId === 'function' && pid) {
        const txs = await getTransactionsByPatientId(pid);
        const ipdTxs = (Array.isArray(txs) ? txs : []).filter(x => x.transactionType === 'OUT');
        for (const tx of ipdTxs) {
          result.push({
            type: 'pharmacy',
            description: tx.medicineName || 'Medicine',
            quantity: Math.abs(tx.deductedQuantity || tx.quantity || 1),
            rate: tx.costPerUnit || 0,
            amount: tx.totalCost || (Math.abs(tx.deductedQuantity || tx.quantity || 1) * (tx.costPerUnit || 0)),
            department: 'Pharmacy',
          });
        }
      }
    } catch (_) {}

    // 3) Consumables (optional)
    try {
      const svc = await import('../services/PatientConsumablesService');
      const fn = svc.getConsumablesByAdmissionId || svc.getByAdmissionId || svc.getConsumables;
      if (typeof fn === 'function') {
        const rows = await fn(admId);
        (Array.isArray(rows) ? rows : []).forEach(c => {
          const qty = Number(c.quantity || 1);
          const rate = Number(c.rate || c.price || 0);
          result.push({
            type: 'consumable',
            description: c.name || c.itemName || 'Consumable',
            quantity: qty,
            rate: rate,
            amount: Number(c.amount || (qty * rate)) || 0,
            department: 'Nursing',
          });
        });
      }
    } catch (_) {}

    // 4) Operations/Procedures (optional)
    try {
      const svc = await import('../services/PatientOperationService');
      const fn = svc.getOperationsByAdmissionId || svc.getByAdmissionId || svc.getOperations;
      if (typeof fn === 'function') {
        const rows = await fn(admId);
        (Array.isArray(rows) ? rows : []).forEach(op => {
          const rate = Number(op.cost || op.rate || 0);
          const qty = Number(op.quantity || 1);
          result.push({
            type: 'procedure',
            description: op.operationType || op.name || 'Operation/Procedure',
            quantity: qty,
            rate: rate,
            amount: Number(op.amount || (qty * rate)) || 0,
            department: 'Operation Theatre',
          });
        });
      }
    } catch (_) {}

    // 5) Room/Bed charges (optional)
    try {
      const svc = await import('../services/PatientAdmissionService');
      const fn = svc.getRoomChargesByAdmissionId || svc.getBillingByAdmissionId || svc.getAdmissionById;
      if (typeof fn === 'function') {
        const data = await fn(admId);
        const rows = Array.isArray(data) ? data : [data];
        rows.filter(Boolean).forEach(r => {
          const days = Number(r.days || r.quantity || 1);
          const rate = Number(r.rate || r.roomRate || r.bedRate || 0);
          const desc = r.roomType || r.bedType || r.description || 'Room/Bed Charges';
          if (rate > 0) {
            result.push({
              type: 'room',
              description: desc,
              quantity: days,
              rate: rate,
              amount: Number(r.amount || (days * rate)) || 0,
              department: 'Room Rent',
            });
          }
        });
      }
    } catch (_) {}

    return result;
  };

  // Merge duplicate services: remove exact duplicates, merge similar entries
  const mergeDuplicateServices = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    
    // First pass: Remove exact duplicates (same type, description, rate, quantity, amount)
    const seenExact = new Set();
    const deduplicated = [];
    
    items.forEach(s => {
      if (!s) return;
      const qty = Number(s.quantity || 1);
      const rate = Number(s.rate || 0);
      const amount = Number(s.amount || (qty * rate)) || 0;
      const desc = String(s.description || '').trim();
      const type = String(s.type || '').trim();
      
      // Create exact match key (all key fields)
      const exactKey = `${type}|${desc}|${rate}|${qty}|${amount}`;
      
      if (!seenExact.has(exactKey)) {
        seenExact.add(exactKey);
        deduplicated.push({
          ...s,
          quantity: qty,
          rate: rate,
          amount: amount,
        });
      }
    });
    
    // Second pass: Merge entries with same type+description+rate but different quantities/amounts
    // (This handles cases where same service has multiple distinct entries)
    const mergeMap = new Map();
    
    deduplicated.forEach(s => {
      const desc = String(s.description || '').trim();
      const type = String(s.type || '').trim();
      const rate = Number(s.rate || 0);
      const key = `${type}|${desc}|${rate}`;
      
      if (!mergeMap.has(key)) {
        mergeMap.set(key, { ...s });
      } else {
        const curr = mergeMap.get(key);
        const newQty = curr.quantity + s.quantity;
        const newAmount = Number(curr.amount || 0) + Number(s.amount || 0);
        mergeMap.set(key, {
          ...curr,
          quantity: newQty,
          amount: newAmount,
        });
      }
    });
    
    return Array.from(mergeMap.values());
  };

  const calculateTotalAmount = () => {
    return services.reduce((total, service) => {
      return total + (service.amount || 0);
    }, 0);
  };

  const handleGenerateBill = async () => {
    if (!paymentMode.mode) {
      alert('Please select a payment mode');
      return;
    }

    setLoading(true);
    try {
      // Get user's organizationId (hospital organization) for billing
      const userOrganizationId = getOrganizationId(user);
      
      // Get tariff for billing
      // For cash: use user's organization tariff
      // For credit: use credit organization's tariff
      const tariff = await getTariffForBilling(
        paymentMode.mode, 
        paymentMode.mode === 'credit' ? paymentMode.organizationId : userOrganizationId
      );

      // Handle null tariff ID (some tariffs may have null id)
      const tariffId = tariff?.id !== null && tariff?.id !== undefined ? tariff.id : null;
      
      if (!tariffId && paymentMode.mode === 'credit') {
        alert('Tariff ID is required for credit billing. Please ensure organization has a tariff assigned.');
        return;
      }

      // Always send hospital org ID for table routing (admission lookup)
      // For credit payments, pass credit org ID separately
      const creditOrgId = paymentMode.mode === 'credit' ? paymentMode.organizationId : null;

      console.log('[IPDDischargeBilling] Generating bill - organizationId:', userOrganizationId, 'tariffId:', tariffId, 'paymentMode:', paymentMode.mode, 'creditOrgId:', creditOrgId);

      const bill = await generateIPDDischargeBill(
        admissionId,
        tariffId,
        paymentMode.mode,
        userOrganizationId,
        services,
        user?.id || null,
        creditOrgId
      );

      setBilling(bill);
      setTariffInfo(tariff);
      setShowPaymentDialog(true);
    } catch (error) {
      console.error('Error generating bill:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to generate bill. Please check backend logs.';
      alert('Error generating bill: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async () => {
    if (!billing) return;

    setProcessingPayment(true);
    try {
      if (paymentMode.mode === 'cash') {
        // Process cash payment
        try {
          await processCashPayment(billing.id, {
            amount: billing.totalAmount,
            paymentMethod: 'cash',
            receivedBy: user?.id || 'frontdesk'
          });
          
          // Create invoice record for cash payment (similar to credit payment)
          // Use user's organizationId (hospital organization) for cash payments
          if (patientId && billing.totalAmount) {
            try {
              const userOrganizationId = getOrganizationId(user);
              await createInvoiceRecord({
                patientId: patientId,
                doctorId: treatingDoctor?.id || treatingDoctor?.doctorId || undefined,
                amount: billing.totalAmount,
                totalAmount: billing.totalAmount,
                organizationId: userOrganizationId, // Use hospital organization for cash payments
                paymentMode: 'CASH',
                paymentStatus: 'PAID',
                billType: 'IPD',
                admissionId: admissionId,
                date: new Date().toISOString()
              });
            } catch (invError) {
              // Invoice creation is optional - log warning but don't fail the flow
              console.warn('Invoice record creation failed for cash payment (optional):', invError);
            }
          }
          
          alert('Cash payment processed successfully!');
        } catch (error) {
          // Cash endpoint missing: fall back to updating bill status directly
          try {
            await updateBill(billing.id, { paymentStatus: 'PAID' });
            
            // Create invoice record even when using fallback
            // Use user's organizationId (hospital organization) for cash payments
            if (patientId && billing.totalAmount) {
              try {
                const userOrganizationId = getOrganizationId(user);
                await createInvoiceRecord({
                  patientId: patientId,
                  doctorId: treatingDoctor?.id || treatingDoctor?.doctorId || undefined,
                  amount: billing.totalAmount,
                  totalAmount: billing.totalAmount,
                  organizationId: userOrganizationId, // Use hospital organization for cash payments
                  paymentMode: 'CASH',
                  paymentStatus: 'PAID',
                  billType: 'IPD',
                  admissionId: admissionId,
                  date: new Date().toISOString()
                });
              } catch (invError) {
                console.warn('Invoice record creation failed for cash payment (optional):', invError);
              }
            }
            
            alert('Cash payment recorded. Bill marked as PAID.');
          } catch (e2) {
            console.warn('Failed to mark bill paid via update:', e2);
            alert('Bill created successfully, but payment endpoint is unavailable. Please settle from billing screen.');
          }
        }
      } else if (paymentMode.mode === 'credit') {
        // For credit billing, the bill is already created with organizationId and OUTSTANDING status
        // The invoice tracking is optional - bill header already contains all necessary info
        try {
          // Optionally create an invoice record if needed (using existing invoice service)
          // This is optional since the bill header already has organizationId
          if (patientId && billing.totalAmount) {
            try {
              await createInvoiceRecord({
                patientId: patientId,
                doctorId: treatingDoctor?.id || treatingDoctor?.doctorId || undefined,
                amount: billing.totalAmount,
                totalAmount: billing.totalAmount,
                organizationId: paymentMode.organizationId,
                paymentMode: 'CREDIT',
                paymentStatus: 'OUTSTANDING',
                billType: 'IPD',
                admissionId: admissionId,
                date: new Date().toISOString()
              });
            } catch (invError) {
              // Invoice creation is optional - bill already has organization info
              console.warn('Invoice record creation failed (optional):', invError);
            }
          }
        } catch (error) {
          console.warn('Credit invoice processing warning:', error);
        }
        alert('Credit bill created successfully! Bill is tagged to ' + (paymentMode.organizationName || 'organization') + ' and marked as outstanding.');
      }

      onBillGenerated?.(billing);
      onClose?.();
    } catch (error) {
      console.error('Error processing payment:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to process payment';
      alert('Error processing payment: ' + errorMsg);
    } finally {
      setProcessingPayment(false);
    }
  };

  const getServiceTypeColor = (type) => {
    return 'default';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const handlePrintBill = () => {
    if (!billing) return;
    const displayPatientName =
      resolvedPatientName ||
      billing?.patientName ||
      billing?.patient?.name ||
      patientName ||
      'N/A';

    const bodyHtml = buildInvoiceDocumentHtml({
      hospitalName: organizationName || billing?.organizationName || user?.organizationName || 'Hospital',
      title: 'IPD DISCHARGE BILL',
      metaLines: [
        { label: 'Bill ID', value: billing.id || 'N/A' },
        { label: 'Date', value: new Date().toLocaleString('en-IN') },
        { label: 'Patient', value: displayPatientName },
        { label: 'Patient ID', value: patientId || billing.patientId || 'N/A' },
        { label: 'Admission ID', value: admissionId || billing.admissionId || 'N/A' },
        { label: 'Treating Doctor', value: treatingDoctor?.name || 'N/A' },
        { label: 'Payment Status', value: billing.paymentStatus || 'Pending' },
        { label: 'Payment Method', value: paymentMode.mode === 'cash' ? 'CASH' : 'CREDIT' },
      ],
      tableHeaders: ['Service Type', 'Description', 'Quantity', 'Rate (Rs)', 'Amount (Rs)'],
      tableRows: (services || []).map((service) => [
        String((service.type || 'N/A').toUpperCase()),
        service.description || 'N/A',
        String(service.quantity || 1),
        Number(service.rate || 0).toFixed(2),
        Number(service.amount || 0).toFixed(2),
      ]),
      summaryLines: [
        { label: 'Total Amount', value: `Rs ${Number(billing.totalAmount || calculateTotalAmount()).toFixed(2)}` },
        ...(tariffInfo
          ? [{ label: 'Tariff Applied', value: `${tariffInfo.name || 'N/A'} (${tariffInfo.rateType || 'N/A'})` }]
          : []),
      ],
      notes:
        paymentMode.mode === 'credit' && paymentMode.organizationName
          ? `Billed to organization: ${paymentMode.organizationName}`
          : 'This is a computer-generated bill.',
    });

    printPatientSummaryDocument({
      title: `IPD Discharge Bill - ${billing.id}`,
      bodyHtml,
    });
  };

  // Debug: Log current render state
  console.log('[IPDDischargeBilling] Component render - Services:', services?.length || 0, 'Doctor:', treatingDoctor?.name || 'null', 'Loading:', loading);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" gutterBottom>
        IPD Discharge Billing
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Patient: {resolvedPatientName || patientName || 'N/A'} | Admission ID: {admissionId}
      </Typography>

      {/* Payment Mode Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Mode Selection
        </Typography>
        <PaymentModeSelector
          value={paymentMode}
          onChange={setPaymentMode}
          showTariffInfo={true}
        />
      </Paper>

      {/* Services Summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Services Summary
          </Typography>
          {services && services.length > 0 && (
            <Chip 
              label={`${services.length} Service${services.length > 1 ? 's' : ''}`} 
              color="primary" 
              size="small"
            />
          )}
        </Box>
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress />
          </Box>
        ) : services && Array.isArray(services) && services.length > 0 ? (
          <Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Service Type</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell><strong>Quantity</strong></TableCell>
                    <TableCell><strong>Rate</strong></TableCell>
                    <TableCell><strong>Amount</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.map((service, index) => {
                    // Debug log for each service
                    if (index === 0) {
                      console.log('[IPDDischargeBilling] Rendering services, count:', services.length, 'First service:', service);
                    }
                    return (
                      <TableRow 
                        key={`service-${index}-${service.type}-${service.description}`}
                        sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                      >
                        <TableCell>
                          <Chip 
                            label={service.type?.toUpperCase() || 'N/A'} 
                            color={getServiceTypeColor(service.type)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell><strong>{service.description || 'N/A'}</strong></TableCell>
                        <TableCell>{service.quantity || 1}</TableCell>
                        <TableCell>{formatCurrency(service.rate || 0)}</TableCell>
                        <TableCell><strong>{formatCurrency(service.amount || 0)}</strong></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Alert severity="info">
            No services found for this admission. 
            {loading ? ' Loading...' : ' Services will be aggregated from lab tests, medicines, consumables, and operations.'}
            <br />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Services state: {services ? `${services.length} services` : 'null/undefined'} | Loading: {loading ? 'true' : 'false'}
            </Typography>
          </Alert>
        )}

        {services && services.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <Typography variant="h6">
                  Total Amount: {formatCurrency(calculateTotalAmount())}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  startIcon={<ReceiptIcon />}
                  onClick={handleGenerateBill}
                  disabled={loading || !paymentMode.mode || !!billing}
                >
                  {billing ? 'Bill Generated' : 'Generate Bill'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Payment Processing Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Process Payment - {paymentMode.mode === 'cash' ? 'Cash Payment' : 'Credit Billing'}
        </DialogTitle>
        <DialogContent>
          {billing && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Bill Generated Successfully
                </Typography>
                <Typography variant="body2">
                  Bill ID: {billing.id} | Amount: {formatCurrency(billing.totalAmount)}
                </Typography>
                {tariffInfo && (
                  <Typography variant="body2">
                    Tariff: {tariffInfo.name} ({tariffInfo.rateType})
                  </Typography>
                )}
              </Alert>

              {paymentMode.mode === 'cash' ? (
                <Alert severity="success">
                  <Typography variant="body2">
                    Patient will pay immediately at the counter. Receipt will be generated.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning">
                  <Typography variant="body2">
                    Bill will be tagged to {paymentMode.organizationName}. 
                    Invoice will be sent to the organization for payment.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePrintBill}
            variant="outlined"
            startIcon={<PrintIcon />}
            disabled={!billing}
          >
            Print Bill
          </Button>
          <Button
            onClick={handleProcessPayment}
            variant="contained"
            disabled={processingPayment}
            startIcon={processingPayment ? <CircularProgress size={20} /> : 
              (paymentMode.mode === 'cash' ? <ReceiptIcon /> : <SendIcon />)}
          >
            {processingPayment ? 'Processing...' : 
              (paymentMode.mode === 'cash' ? 'Process Cash Payment' : 'Create Credit Bill')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IPDDischargeBilling;
